import { Column, CustomRule, Editing, Lookup, RequiredRule } from 'devextreme-react/data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import DatagridCustom from '../../components/common/datagrid/custom-datagrid';
import api from '../../services/api-configuration';
import { getAllWarehouses } from '../../services/apis';

const AdreslemeConfig = () => {
    const [dataSources, setDataSources] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const { hasRole } = useAuth(6);
    const [loadError, setLoadError] = useState(null);

    const DIRECTION = [
        { direction: 'START', name: 'BAŞTAN' },
        { direction: 'END', name: 'SONDAN' }
    ];

    const ilerleme_yonu = [
        { scan_type: 'VERTICAL', name: 'DİKEY' },
        { scan_type: 'HORIZONTAL', name: 'YATAY' }
    ];

    const TYPE = [
        { algorithm_type: 'FILL_ALL', name: 'TAMAMINI DOLDUR' },
        { algorithm_type: 'X_COUNT', name: 'ADET GİR' }
    ];

    const fetchData = useCallback(async () => {
        try {
            const [warehousesRes, response] = await Promise.all([
                getAllWarehouses(), api.post('/autoAddressing/getAll')
            ]);
            // Keep legacy rules identifiable even when their warehouse master record is missing.
            const missingCodes = [...new Set(response.data.map(row => row.warehouse_code))]
                .filter(code => !warehousesRes.some(warehouse => warehouse.warehouse_code === code));
            setWarehouses([...warehousesRes, ...missingCodes.map(code => ({
                warehouse_code: code, warehouse_name: `${code} (depo tanımı bulunamadı)`
            }))]);
            setDataSources(response.data);
            setLoadError(null);
        } catch (error) {
            setLoadError('Adresleme kuralları yüklenemedi. Yeniden deneyin.');
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const persist = async (row) => {
        try {
            await api.post('/autoAddressing/update', {
                id: row.id, algorithm_type: row.algorithm_type,
                direction: row.direction, scan_type: row.scan_type,
                x_count: row.algorithm_type === 'X_COUNT' ? row.x_count : null
            });
            toast.success('Adresleme kuralı kaydedildi.');
            return false;
        } catch (error) {
            toast.error(error.response?.data?.error || 'Adresleme kuralı kaydedilemedi.');
            return true;
        }
    };

    return (
        <>
            <Breadcrumb current="Adresleme Konfigürasyonu" />

            <div className="container-fluid card p-4 m-2">
                <div className="mb-3">
                    <button className="btn btn-outline-primary" onClick={fetchData}>Yenile</button>
                    <span className="ml-3">Mevcut kurallar: {dataSources.length}</span>
                </div>
                {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}
                <DatagridCustom
                    dataSource={dataSources}
                    keyExpr="id"
                    onRowUpdating={(e) => { e.cancel = persist({ ...e.oldData, ...e.newData }); }}
                    onRowUpdated={fetchData}
                    onEditorPreparing={(e) => {
                        if (e.parentType === 'dataRow' && e.dataField === 'x_count') {
                            e.editorOptions.disabled = e.row.data.algorithm_type !== 'X_COUNT';
                        }
                    }}
                >
                    <Editing
                        mode="row"
                        allowUpdating={hasRole('adres_role_write')}
                        useIcons
                    />
                    <Column
                        caption='Depo Kodu'
                        dataField='warehouse_code'
                        allowEditing={false}
                        alignment='center'
                    >
                        <Lookup
                            dataSource={warehouses}
                            valueExpr="warehouse_code"
                            displayExpr="warehouse_name"
                        />
                    </Column>
                    <Column dataField="zone_type" caption="Bölge Türü" allowEditing={false}>
                        <Lookup dataSource={[
                            { value: 'TYPE_STOCK', name: 'Stok Alanı' },
                            { value: 'TYPE_SHIPMENTAREA', name: 'Sevkiyat Alanı' }
                        ]} valueExpr="value" displayExpr="name" />
                    </Column>
                    <Column dataField="is_active" caption="Aktif" dataType="boolean" allowEditing={false} />
                    <Column
                        caption='Algoritma Türü' 
                        dataField='algorithm_type'
                        alignment='center'
                        setCellValue={(rowData, value) => {
                            rowData.algorithm_type = value;
                            if (value === 'FILL_ALL') {
                                rowData.x_count = null;
                            }
                            return rowData;
                        }}
                    >
                        <Lookup
                            dataSource={TYPE}
                            valueExpr="algorithm_type"
                            displayExpr="name"
                        />
                        <RequiredRule message="Bu alan zorunludur." />
                    </Column>
                    <Column
                        caption='Adet'
                        dataField='x_count'
                        alignment='center'
                        dataType="number"
                        editorOptions={{ min: 1, format: '#0' }}
                    >
                        <CustomRule reevaluate message="Adet pozitif tam sayı olmalıdır."
                            validationCallback={(e) => e.data.algorithm_type !== 'X_COUNT' || (Number.isInteger(e.value) && e.value > 0)} />
                    </Column>
                    <Column
                        caption='Yön'
                        dataField='direction'
                        alignment='center'
                    >
                        <Lookup
                            dataSource={DIRECTION}
                            valueExpr="direction"
                            displayExpr="name"
                        />
                        <RequiredRule message="Bu alan zorunludur." />
                    </Column>
                    <Column
                        caption='İlerleme Yönü'
                        dataField='scan_type'
                        alignment='center'
                    >
                        <Lookup
                            dataSource={ilerleme_yonu}
                            valueExpr="scan_type"
                            displayExpr="name"
                        />
                        <RequiredRule message="Bu alan zorunludur." />
                    </Column>
                </DatagridCustom>
            </div>
            <hr />
        </>
    );
};

export default AdreslemeConfig;
